/**
 * EventBus — Singleton publish/subscribe event system.
 * Scenes and systems communicate exclusively through EventBus.
 * Never hold direct references between scenes.
 *
 * @module EventBus
 *
 * @example
 * // Subscribe
 * const unsub = EventBus.on('combat:end', ({ winner }) => { ... });
 * // Later: unsub(); to remove listener
 *
 * // Emit
 * EventBus.emit('combat:end', { winner: 'party' });
 *
 * // One-time
 * EventBus.once('scene:change', (data) => { ... });
 */

const EventBus = {
  /** @type {Record<string, Function[]>} */
  _listeners: {},

  /**
   * Subscribe to an event. Returns an unsubscribe function.
   * @param {string} event
   * @param {Function} fn
   * @returns {Function} unsubscribe
   */
  on(event, fn) {
    (this._listeners[event] ??= []).push(fn);
    return () => this.off(event, fn);
  },

  /**
   * Unsubscribe a specific listener from an event.
   * @param {string} event
   * @param {Function} fn
   */
  off(event, fn) {
    this._listeners[event] = (this._listeners[event] || []).filter(f => f !== fn);
  },

  /**
   * Emit an event, calling all registered listeners with the provided data.
   * @param {string} event
   * @param {*} [data]
   */
  emit(event, data) {
    const listeners = this._listeners[event];
    if (!listeners || listeners.length === 0) return;
    // Snapshot the array so mutations during iteration are safe
    [...listeners].forEach(fn => {
      try {
        fn(data);
      } catch (err) {
        // Avoid crashing other listeners; let global onerror capture it
        // eslint-disable-next-line no-console
        console.error(`[EventBus] Listener error on "${event}":`, err);
      }
    });
  },

  /**
   * Subscribe to an event once — listener is automatically removed after first call.
   * @param {string} event
   * @param {Function} fn
   * @returns {Function} unsubscribe
   */
  once(event, fn) {
    const wrap = (data) => {
      fn(data);
      this.off(event, wrap);
    };
    return this.on(event, wrap);
  },

  /**
   * Remove all listeners for a given event, or all listeners if no event specified.
   * @param {string} [event]
   */
  clear(event) {
    if (event) {
      delete this._listeners[event];
    } else {
      this._listeners = {};
    }
  },
};

export default EventBus;
